/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import qs from 'qs';

/**
 * Provides a utility for parsing and returning feature flag parameters passed
 * to the applications Query String. By default, this function will validate
 * and parse a `features` query string parameter for values. Alternatively,
 * an optional query string parameter can be provided if needed.
 *
 * Retrieve feature flags for a the default `feature` query string parameter:
 *
 * ```typescript
 *   // "https://app.calam.com?features=feature-a,feature-b
 *
 *   import {getFeatureFlags} from '@ion/applications/routes';
 *
 *   const featureFlags = getFeatureFlags();
 *   // [feature-a, feature-b]
 * ```
 *
 * Retrieve feature flags for a custom query string feature flag parameter:
 *
 * ```typescript
 *   // "https://app.calam.com?feature-flags=feature-a,feature-b
 *
 *   import {getFeatureFlags} from '@ion/applications/routes';
 *
 *   const featureFlags = getFeatureFlags('feature-flags');
 *   // [feature-a, feature-b]
 * ```
 *
 * Note, while in local Development Mode, the `features` query parameter can
 * be automatically populated by passing a `--features` argument when running
 * `yarn start`, which will  result in the given feature(s) automatically being
 * added to the application query string.
 *
 * ```typescript
 * # start the app with the 'feature-a' and 'feature-b' features enabled
 * yarn start --features=feature-a,feature-b
 * ```
 *
 * Given the above, the application will start with the feature-a, and
 * feature-a features added to the `features` query parameter:
 * `?features=feature-a,feature-b`.
 *
 * @param   An optional query string parameter name from which to parse
 *          the provided feature flags from the query string.
 * @returns An array of feature flags passed in to the given query string
 *          parameter (if any), otherwise returns an empty array.
 */
export const getFeatureFlags = (param = 'features') => {
  const {search} = window?.location || {};
  const features = (qs.parse(search, {ignoreQueryPrefix: true}) || {})[param];
  if (features) {
    return features
      .toString()
      .replace(/\s/g, '')
      .split(/,| /);
  }
  return [];
};

enum pageTitleInCamelCase {
  SYSTEMCONFIG_NOTIFICATION_TYPE = 'systemconfigNotificationtype',
  ADMIN_DEVICE_CONFIGS = 'adminDeviceconfigs',
  ADMIN_PEG_BEHAVIORS = 'adminPegbehaviors',
  ADMIN_PEG_LABELS = 'adminPeglabels'
};

const modifiedPageTitles = {
  [pageTitleInCamelCase.SYSTEMCONFIG_NOTIFICATION_TYPE] : 'systemconfigNotificationType',
  [pageTitleInCamelCase.ADMIN_DEVICE_CONFIGS]           : 'adminDeviceConfigs',
  [pageTitleInCamelCase.ADMIN_PEG_BEHAVIORS]            : 'adminPegBehaviors',
  [pageTitleInCamelCase.ADMIN_PEG_LABELS]               : 'adminPegLabels'
};

/**
 * The getModifiedPageTitlesForMobile function provides a simple utility
 * which returns the modified pageTitle for the given route pageTitle
 * that is not match with the page title described in the 'nav:side' inside nav.json file.
 *
 * ```typescript
 *
 *   import {getModifiedPageTitlesForMobile} from '@ion/applications/src/routes/RouteUtils';
 *
 *   const pageTitle = getModifiedPageTitlesForMobile(currentPageTitle);
 *
 * ```
 */
export const getModifiedPageTitlesForMobile = (currentPageTitle: string) => {
  const result = Object.values(pageTitleInCamelCase).find(title => title === currentPageTitle);
  return !result ? currentPageTitle : modifiedPageTitles[result];
};
